import React, { lazy, Suspense, FC } from 'react';
import { useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import LoadingPage from '../components/layouts/LoadingPage';
import MainBase from '../components/layouts/MainBase';
import MainBaseUser from '../components/userLayouts/MainBase';
import { RootState } from '../stores';
import {
  isRouteToClient,
} from '../utils/roles/routes/default';

// Wrapper for lazy-loaded components
const LazyWrapper = (Component: React.LazyExoticComponent<React.FC>) => (props: any) => (
  <Suspense fallback={<LoadingPage />}>
    <Component {...props} />
  </Suspense>
);

// ========================
// Lazy Imports
// ========================

// -- Admin
const Dashboard = LazyWrapper(lazy(() => import('../pages/Dashboard')));
const Berkas = LazyWrapper(lazy(() => import('../pages/Berkas/ViewBerkas')));
const BerkasDetail = LazyWrapper(lazy(() => import('../pages/Berkas/BerkasDetail')));
const BerkasSK = LazyWrapper(lazy(() => import('../pages/Berkas/BerkasSK')));
const BerkasSKDetail = LazyWrapper(lazy(() => import('../pages/Berkas/BerkasSKDetail')));
const BerkasFinal = LazyWrapper(lazy(() => import('../pages/Berkas/BerkasFinal')));
const BerkasFinalDetail = LazyWrapper(lazy(() => import('../pages/Berkas/BerkasFinalDetail')));

// Pengesahan
const PengesahanKasi = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanKasi')));
const PengesahanDetail = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanKasiDetail')));
const PengesahanKabid = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanKabid')));
const PengesahanKabidDetail = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanKabidDetail')));
const PengesahanKadis = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanKadis')));
const PengesahanKadisDetail = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanKadisDetail')));
const PengesahanSekdis = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanSekdis')));
const PengesahanSekdisDetail = LazyWrapper(lazy(() => import('../pages/Pengesahan/PengesahanSekdisDetail')));

// Penugasan
const PenugasanSurvei = LazyWrapper(lazy(() => import('../pages/Penugasan/PenugasanSurvei')));
const PenugasanSurveiDetail = LazyWrapper(lazy(() => import('../pages/Penugasan/PenugasanSurveiDetail')));
const PenugasanGambar = LazyWrapper(lazy(() => import('../pages/Penugasan/PenugasanGambar')));
const PenugasanGambarDetail = LazyWrapper(lazy(() => import('../pages/Penugasan/PenugasanGambarDetail')));
const PenugasanOverlay = LazyWrapper(lazy(() => import('../pages/Penugasan/PenugasanOverlay')));
const PenugasanOverlayDetail = LazyWrapper(lazy(() => import('../pages/Penugasan/PenugasanOverlayDetail')));


// Pengiriman
const ViewPengiriman = LazyWrapper(lazy(() => import('../pages/Pengiriman/ViewPengiriman')));

// Penggambaran
const ViewPenggambaran = LazyWrapper(lazy(() => import('../pages/Penggambaran/ViewPenggambaran')));
const PenggambaranDetail = LazyWrapper(lazy(() => import('../pages/Penggambaran/PenggambaranDetail')));

// Overlay
const ViewOverlay = LazyWrapper(lazy(() => import('../pages/Overlay/ViewOverlay')));
const OverlayDetail = LazyWrapper(lazy(() => import('../pages/Overlay/OverlayDetail')));

// Others 
const ReportView = LazyWrapper(lazy(() => import('../pages/Reports/ReportView')));
const ReportDetail = LazyWrapper(lazy(() => import('../pages/Reports/ReportDetail')));
const Survei = LazyWrapper(lazy(() => import('../pages/Survei/ViewSurvei')));
const SurveiDetail = LazyWrapper(lazy(() => import('../pages/Survei/SurveiDetail')));
const ParameterBerkas = LazyWrapper(lazy(() => import('../pages/ParameterBerkas')));
const FilterBerkas = LazyWrapper(lazy(() => import('../pages/FilterBerkas')));
const Sliders = LazyWrapper(lazy(() => import('../pages/Sliders')));
const UserManagement = LazyWrapper(lazy(() => import('../pages/Users/UserManagement')));
const UserModify = LazyWrapper(lazy(() => import('../pages/Users/UserModify')));
const UserNew = LazyWrapper(lazy(() => import('../pages/Users/UserNew')));
const UserRoles = LazyWrapper(lazy(() => import('../pages/Users/UserRoles')));

// -- Client
const DashboardClient = LazyWrapper(lazy(() => import('../pages/ClientPage/Dashboard')));
const Permohonan = LazyWrapper(lazy(() => import('../pages/ClientPage/Permohonan')));
const Tinjauan = LazyWrapper(lazy(() => import('../pages/ClientPage/Tinjauan')));
const UpdateData = LazyWrapper(lazy(() => import('../pages/ClientPage/UpdateData')));
const UpdateFile = LazyWrapper(lazy(() => import('../pages/ClientPage/UpdateFile')));
const BantuanClient = LazyWrapper(lazy(() => import('../pages/ClientPage/Bantuan')));
const DownloadForm = LazyWrapper(lazy(() => import('../pages/ClientPage/DownloadForm')));

// -- Landing
const LandingPageDashboard = LazyWrapper(lazy(() => import('../pages/LandingPage/Dashboard')));
const Bantuan = LazyWrapper(lazy(() => import('../pages/LandingPage/Bantuan')));
const Tutorial = LazyWrapper(lazy(() => import('../pages/LandingPage/Tutorial')));
const TutorialVideo = LazyWrapper(lazy(() => import('../pages/LandingPage/TutorialVideo')));

// -- Auth / General
const Login = LazyWrapper(lazy(() => import('../pages/Login')));
const Register = LazyWrapper(lazy(() => import('../pages/Register')));
const ForgetPassword = LazyWrapper(lazy(() => import('../pages/ForgetPassword')));
const ResetPassword = LazyWrapper(lazy(() => import('../pages/ResetPassword')));
const NotFound = LazyWrapper(lazy(() => import('../pages/NotFound')));

// ========================
// Protected Routes
// ========================

const ProtectedRoutes: FC<{ user: User }> = ({ user }) => {
  const location = useLocation();

  if (user.id === -1 && location.pathname !== '/login') {
    return <Navigate replace to="/login" />;
  }

  if (!isRouteToClient(user)) {
    return (
      <MainBase>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/berkas" element={<Berkas />} />
          <Route path="/berkas/:id" element={<BerkasDetail />} />
          <Route path="/berkas-sk" element={<BerkasSK />} />
          <Route path="/berkas-sk/:id" element={<BerkasSKDetail />} />
          <Route path="/berkas-final" element={<BerkasFinal />} />
          <Route path="/berkas-final/:id" element={<BerkasFinalDetail />} />
          <Route path="/penugasan" element={<PenugasanSurvei />} />
          <Route path="/penugasan/survey/:id" element={<PenugasanSurveiDetail />} />
          <Route path="/penugasan/pengggambaran" element={<PenugasanGambar />} />
          <Route path="/penugasan/pengggambaran/:id" element={<PenugasanGambarDetail />} />
          <Route path="/penugasan/overlay" element={<PenugasanOverlay />} />
          <Route path="/penugasan/overlay/:id" element={<PenugasanOverlayDetail />} />
          <Route path="/penggambaran" element={<ViewPenggambaran />} />
          <Route path="/penggambaran/:id" element={<PenggambaranDetail />} />
          <Route path="/overlay" element={<ViewOverlay />} />
          <Route path="/overlay/:id" element={<OverlayDetail />} />
          <Route path="/pengesahan" element={<PengesahanKasi />} />
          <Route path="/pengesahan/kabid" element={<PengesahanKabid />} />
          <Route path="/pengesahan/kabid/:id" element={<PengesahanKabidDetail />} />
          <Route path="/pengesahan/sekdis" element={<PengesahanSekdis />} />
          <Route path="/pengesahan/sekdis/:id" element={<PengesahanSekdisDetail />} />
          <Route path="/pengesahan/kadis" element={<PengesahanKadis />} />
          <Route path="/pengesahan/kadis/:id" element={<PengesahanKadisDetail />} />
          <Route path="/pengesahan/:id" element={<PengesahanDetail />} />
          <Route path="/reports" element={<ReportView />} />
          <Route path="/reports/:id" element={<ReportDetail />} />
          <Route path="/pengiriman" element={<ViewPengiriman />} />
          <Route path="/survey" element={<Survei />} />
          <Route path="/survey/:id" element={<SurveiDetail />} />
          <Route path="/config" element={<Sliders />} />
          <Route path="/config/parameter-berkas" element={<ParameterBerkas />} />
          <Route path="/config/filter-berkas" element={<FilterBerkas />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/users/new" element={<UserNew />} />
          <Route path="/users/roles" element={<UserRoles />} />
          <Route path="/users/:id" element={<UserModify />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </MainBase>
    );
  }

  return (
    <MainBaseUser>
      <Routes>
        <Route path="/dashboard" element={<DashboardClient />} />
        <Route path="/permohonan" element={<Permohonan />} />
        <Route path="/tinjauan" element={<Tinjauan />} />
        <Route path="/tinjauan/data/:id" element={<UpdateData />} />
        <Route path="/tinjauan/file/:id" element={<UpdateFile />} />
        <Route path="/bantuan-page" element={<BantuanClient />} />
        <Route path="/form" element={<DownloadForm />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </MainBaseUser>
  );
};

// ========================
// App Routes
// ========================
const AppRoutes: FC = () => {
  const { user } = useSelector((s: RootState) => s.user);

  return (
    <Routes>
      <Route index element={<LandingPageDashboard />} />
      <Route path="login" element={<Login />} />
      <Route path="register" element={<Register />} />
      <Route path="forget" element={<ForgetPassword />} />
      <Route path="password/reset/:string" element={<ResetPassword />} />
      <Route path="/tutorial" element={<Tutorial />} />
      <Route path="/tutorial/video" element={<TutorialVideo />} />
      <Route path="/bantuan" element={<Bantuan />} />
      <Route path="*" element={<ProtectedRoutes user={user} />} />
    </Routes>
  );
};

export default AppRoutes;
